import Base from '../base/base';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';

gsap.registerPlugin(ScrollTrigger);

interface TextLineFadeOnScrollOptions {
  rootSelector?: string;
  targetSelector?: string;
  start?: string;
  duration?: number;
  stagger?: number;
  y?: number;
  ease?: string;
  once?: boolean;
  debug?: boolean;
  [key: string]: any;
}

interface FadeInstance {
  splits: SplitType[];
  tween: gsap.core.Tween;
}

export default class TextLineFadeOnScroll extends Base {
  private config: Required<Omit<TextLineFadeOnScrollOptions, 'debug'>>;
  private instances: FadeInstance[] = [];

  constructor(options: TextLineFadeOnScrollOptions = {}) {
    super(options.debug || false);

    this.config = {
      rootSelector: '[data-fade-lines]',
      targetSelector: '[data-fade-item], p, h1, h2, h3, h4, h5, h6, li, blockquote',
      start: 'top 70%',
      duration: 0.85,
      stagger: 0.08,
      y: 20,
      ease: 'power4.out',
      once: true,
      ...options
    };

    this.init();
  }

  init(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.log('Reduced motion is enabled; skipping line fade animation.');
      return;
    }

    const roots = Array.from(document.querySelectorAll<HTMLElement>(this.config.rootSelector));
    if (!roots.length) {
      this.log('No data-fade-lines containers found.');
      return;
    }

    roots.forEach((root) => this.createInstance(root));
    ScrollTrigger.refresh();
  }

  private createInstance(root: HTMLElement): void {
    let targets = Array.from(root.querySelectorAll<HTMLElement>(this.config.targetSelector));

    if (!targets.length && root.textContent?.trim()) {
      targets = [root];
    }

    targets = targets.filter((target) => (target.textContent || '').trim().length > 0);

    if (!targets.length) {
      this.log('No text targets found for container:', root);
      return;
    }

    const splitTargets = targets.filter((target) => !this.containsHardBreak(target));
    const preserveTargets = targets.filter((target) => this.containsHardBreak(target));

    const splits = splitTargets.map((target) => new SplitType(target, { types: 'lines' }));
    const lines = splits.flatMap((split) => (split.lines || []) as HTMLElement[]);
    const animationTargets = [...lines, ...preserveTargets];

    if (!animationTargets.length) {
      splits.forEach((split) => split.revert());
      return;
    }

    gsap.set(animationTargets, { opacity: 0, y: this.config.y, willChange: 'transform,opacity' });

    const tween = gsap.to(animationTargets, {
      opacity: 1,
      y: 0,
      duration: this.config.duration,
      stagger: this.config.stagger,
      ease: this.config.ease,
      clearProps: 'willChange',
      scrollTrigger: {
        trigger: root,
        start: this.config.start,
        once: this.config.once,
        toggleActions: this.config.once ? 'play none none none' : 'play none none reverse'
      }
    });

    this.instances.push({ splits, tween });
  }

  private containsHardBreak(target: HTMLElement): boolean {
    const cloned = target.cloneNode(true) as HTMLElement;
    if (!cloned.querySelector('br')) {
      return false;
    }

    let currentNode: ChildNode | null = cloned.lastChild;
    while (currentNode) {
      if (currentNode.nodeType === Node.TEXT_NODE && !(currentNode.textContent || '').trim()) {
        const previous = currentNode.previousSibling;
        currentNode.remove();
        currentNode = previous;
        continue;
      }

      if (currentNode.nodeType === Node.ELEMENT_NODE && (currentNode as Element).tagName.toLowerCase() === 'br') {
        const previous = currentNode.previousSibling;
        currentNode.remove();
        currentNode = previous;
        continue;
      }

      break;
    }

    return cloned.querySelector('br') !== null;
  }

  destroy(): void {
    this.instances.forEach((instance) => {
      instance.tween.kill();
      instance.splits.forEach((split) => split.revert());
    });

    this.instances = [];
  }
}
